import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Icon,
  Snackbar,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import axios from "axios.js";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsAppConversationMessagesPage() {
  const pageTitle = "WhatsApp Conversation";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(
          `/portal/wacc/businesses/numbers/conversations/${ref}/messages.json`
        )
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setMessages(result.data.data.messages || []);
            setConversation(result.data.data.conversation || null);
            setPageLoaded(true);
          } else {
            statusDialog.show(
              result.data.data.message,
              "error",
              "Request Failed"
            );
          }
        })
        .catch(async () => {
          setShowLoading(false);

          await statusDialog.show(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
            "error",
            "Request Failed"
          );
        });
    };

    fetchData();
  }, [ref]);

  const getAuthorName = (message) => {
    return message?.author || "";
  };

  const getAuthorType = (message) => {
    return message?.author_type || "";
  };

  const isBusinessMessage = (message) => {
    const authorType = String(getAuthorType(message)).toLowerCase();

    return (
      authorType === "business" ||
      authorType === "agent" ||
      authorType === "system"
    );
  };

  const getMessageText = (message) => {
    if (!message || !message.content) {
      return "";
    }

    if (typeof message.content === "string") {
      return message.content;
    }

    if (message.content.text) {
      return message.content.text;
    }

    if (message.content.body) {
      return message.content.body;
    }

    if (message.content.caption) {
      return message.content.caption;
    }

    return "";
  };

  const getMediaUrl = (message) => {
    if (!message || !message.content) {
      return null;
    }

    return (
      message.content.url ||
      message.content.link ||
      message.content.media_url ||
      null
    );
  };

  const getDocumentName = (message) => {
    if (!message || !message.content) {
      return "Document";
    }

    return (
      message.content.filename ||
      message.content.file_name ||
      message.content.name ||
      "Document"
    );
  };

  const getMessagePreview = (message) => {
    if (!message) {
      return "";
    }

    const contentType = String(message.content_type || "").toLowerCase();

    if (contentType === "text") {
      return getMessageText(message);
    }

    if (contentType === "image" || contentType === "photo") {
      return "Image";
    }

    if (contentType === "video") {
      return "Video";
    }

    if (contentType === "audio") {
      return "Audio";
    }

    if (contentType === "document" || contentType === "file") {
      return getDocumentName(message);
    }

    if (contentType === "sticker") {
      return "Sticker";
    }

    if (contentType === "location") {
      return "Location";
    }

    if (
      contentType === "contact" ||
      contentType === "contacts"
    ) {
      return "Contact";
    }

    return getMessageText(message);
  };

  const renderReply = (message, outgoing) => {
    if (!message.reply_to) {
      return null;
    }

    const parent = message.reply_to;

    return (
      <MDBox
        mb={1.2}
        px={1.5}
        py={1}
        sx={{
          borderLeft: "4px solid rgba(0, 0, 0, 0.28)",
          backgroundColor: outgoing
            ? "rgba(255, 255, 255, 0.45)"
            : "rgba(0, 0, 0, 0.05)",
          borderRadius: "6px",
        }}
      >
        <MDTypography
          sx={{
            display: "block",
            fontSize: "13px",
            lineHeight: 1.35,
            fontWeight: 600,
            color: outgoing ? "#075e54" : "#444444",
          }}
        >
          {getAuthorName(parent)}
        </MDTypography>

        <MDTypography
          sx={{
            display: "block",
            fontSize: "11px",
            lineHeight: 1.3,
            opacity: 0.6,
            textTransform: "capitalize",
            mb: 0.3,
          }}
        >
          {getAuthorType(parent)}
        </MDTypography>

        <MDTypography
          sx={{
            display: "block",
            fontSize: "14px",
            lineHeight: 1.45,
            opacity: 0.8,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getMessagePreview(parent)}
        </MDTypography>
      </MDBox>
    );
  };

  const renderMessageContent = (message) => {
    const contentType = String(message.content_type || "").toLowerCase();
    const mediaUrl = getMediaUrl(message);
    const text = getMessageText(message);

    if (contentType === "text") {
      return (
        <MDTypography
          sx={{
            fontSize: "16px",
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "#222222",
          }}
        >
          {text}
        </MDTypography>
      );
    }

    if (contentType === "image" || contentType === "photo") {
      return (
        <MDBox>
          {mediaUrl ? (
            <img
              src={mediaUrl}
              alt="WhatsApp"
              style={{
                display: "block",
                width: "100%",
                maxWidth: "360px",
                maxHeight: "400px",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          ) : (
            <MDBox display="flex" alignItems="center" py={2}>
              <Icon sx={{ mr: 1 }}>image</Icon>

              <MDTypography sx={{ fontSize: "16px" }}>
                Image
              </MDTypography>
            </MDBox>
          )}

          {text && (
            <MDTypography
              sx={{
                mt: 1,
                fontSize: "16px",
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {text}
            </MDTypography>
          )}
        </MDBox>
      );
    }

    if (contentType === "video") {
      return (
        <MDBox>
          {mediaUrl ? (
            <video
              controls
              src={mediaUrl}
              style={{
                display: "block",
                width: "100%",
                maxWidth: "360px",
                maxHeight: "400px",
                borderRadius: "8px",
              }}
            />
          ) : (
            <MDBox display="flex" alignItems="center" py={2}>
              <Icon sx={{ mr: 1 }}>videocam</Icon>

              <MDTypography sx={{ fontSize: "16px" }}>
                Video
              </MDTypography>
            </MDBox>
          )}

          {text && (
            <MDTypography
              sx={{
                mt: 1,
                fontSize: "16px",
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {text}
            </MDTypography>
          )}
        </MDBox>
      );
    }

    if (contentType === "audio") {
      return mediaUrl ? (
        <audio
          controls
          src={mediaUrl}
          style={{
            width: "320px",
            maxWidth: "100%",
          }}
        />
      ) : (
        <MDBox display="flex" alignItems="center" py={1}>
          <Icon sx={{ mr: 1 }}>audiotrack</Icon>

          <MDTypography sx={{ fontSize: "16px" }}>
            Audio
          </MDTypography>
        </MDBox>
      );
    }

    if (contentType === "document" || contentType === "file") {
      return (
        <MDBox
          display="flex"
          alignItems="center"
          px={1.5}
          py={1.4}
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            borderRadius: "7px",
            cursor: mediaUrl ? "pointer" : "default",
          }}
          onClick={() => {
            if (mediaUrl) {
              window.open(mediaUrl, "_blank");
            }
          }}
        >
          <Icon
            sx={{
              mr: 1.5,
              fontSize: "28px",
            }}
          >
            description
          </Icon>

          <MDTypography
            sx={{
              fontSize: "15px",
              wordBreak: "break-word",
            }}
          >
            {getDocumentName(message)}
          </MDTypography>
        </MDBox>
      );
    }

    if (contentType === "sticker") {
      return mediaUrl ? (
        <img
          src={mediaUrl}
          alt="Sticker"
          style={{
            display: "block",
            width: "170px",
            maxHeight: "170px",
            objectFit: "contain",
          }}
        />
      ) : (
        <MDTypography sx={{ fontSize: "16px" }}>
          Sticker
        </MDTypography>
      );
    }

    if (contentType === "location") {
      return (
        <MDBox>
          <MDBox display="flex" alignItems="center">
            <Icon
              sx={{
                mr: 1,
                fontSize: "28px",
              }}
            >
              location_on
            </Icon>

            <MDTypography sx={{ fontSize: "16px" }}>
              {message.content?.name ||
                message.content?.address ||
                "Location"}
            </MDTypography>
          </MDBox>

          {message.content?.latitude &&
            message.content?.longitude && (
              <MDTypography
                sx={{
                  display: "block",
                  mt: 0.7,
                  fontSize: "13px",
                  opacity: 0.65,
                }}
              >
                {message.content.latitude},{" "}
                {message.content.longitude}
              </MDTypography>
            )}
        </MDBox>
      );
    }

    if (
      contentType === "contact" ||
      contentType === "contacts"
    ) {
      return (
        <MDBox display="flex" alignItems="center">
          <Icon
            sx={{
              mr: 1,
              fontSize: "28px",
            }}
          >
            person
          </Icon>

          <MDTypography sx={{ fontSize: "16px" }}>
            {message.content?.name || "Contact"}
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDTypography
        sx={{
          fontSize: "16px",
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text || JSON.stringify(message.content || {})}
      </MDTypography>
    );
  };

  const renderStatus = (message) => {
    if (!isBusinessMessage(message)) {
      return null;
    }

    if (message.read) {
      return (
        <Icon
          sx={{
            fontSize: "16px",
            ml: 0.4,
          }}
        >
          done_all
        </Icon>
      );
    }

    if (message.delivered) {
      return (
        <Icon
          sx={{
            fontSize: "16px",
            ml: 0.4,
          }}
        >
          done_all
        </Icon>
      );
    }

    if (message.sent) {
      return (
        <Icon
          sx={{
            fontSize: "16px",
            ml: 0.4,
          }}
        >
          done
        </Icon>
      );
    }

    return null;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={severity}
          sx={{ width: "100%" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>

      {pageLoaded && (
        <MDBox py={3}>
          <Card sx={{ overflow: "hidden" }}>
            <MDBox
              px={3}
              py={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                borderBottom: "1px solid #eeeeee",
              }}
            >
              <MDBox display="flex" alignItems="center">
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="52px"
                  height="52px"
                  borderRadius="50%"
                  sx={{
                    backgroundColor: "#eeeeee",
                  }}
                >
                  <Icon sx={{ fontSize: "27px" }}>
                    person
                  </Icon>
                </MDBox>

                <MDBox ml={1.5}>
                  <MDTypography
                    sx={{
                      fontSize: "18px",
                      lineHeight: 1.3,
                      fontWeight: 600,
                    }}
                  >
                    {conversation?.contact?.name ||
                      conversation?.contact_name ||
                      "Conversation"}
                  </MDTypography>

                  <MDTypography
                    sx={{
                      fontSize: "13px",
                      lineHeight: 1.4,
                      color: "text.secondary",
                    }}
                  >
                    {conversation?.contact?.phone_number ||
                      conversation?.phone_number ||
                      ""}
                  </MDTypography>

                  {(conversation?.agent?.name ||
                    conversation?.agent_name) && (
                    <MDTypography
                      sx={{
                        fontSize: "13px",
                        lineHeight: 1.4,
                        color: "text.secondary",
                      }}
                    >
                      Agent:{" "}
                      {conversation?.agent?.name ||
                        conversation?.agent_name}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>

              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => navigate(-1)}
              >
                Back
              </MDButton>
            </MDBox>

            <MDBox
              px={{ xs: 1.5, sm: 3, md: 5 }}
              py={3}
              sx={{
                minHeight: "550px",
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                backgroundColor: "#efeae2",
              }}
            >
              {messages.length === 0 ? (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="450px"
                >
                  <MDTypography
                    sx={{
                      fontSize: "15px",
                      color: "text.secondary",
                    }}
                  >
                    No messages in this conversation.
                  </MDTypography>
                </MDBox>
              ) : (
                messages.map((message) => {
                  const outgoing =
                    isBusinessMessage(message);

                  return (
                    <MDBox
                      key={message.reference}
                      display="flex"
                      justifyContent={
                        outgoing
                          ? "flex-end"
                          : "flex-start"
                      }
                      width="100%"
                      mb={1.5}
                    >
                      <MDBox
                        sx={{
                          width: "fit-content",
                          maxWidth: {
                            xs: "90%",
                            sm: "72%",
                            md: "65%",
                          },
                          minWidth: "150px",
                          px: 1.8,
                          pt: 1.4,
                          pb: 0.9,
                          backgroundColor: outgoing
                            ? "#d9fdd3"
                            : "#ffffff",
                          borderRadius: outgoing
                            ? "10px 10px 3px 10px"
                            : "10px 10px 10px 3px",
                          boxShadow:
                            "0 1px 2px rgba(0, 0, 0, 0.14)",
                        }}
                      >
                        <MDBox mb={0.8}>
                          <MDTypography
                            sx={{
                              fontSize: "14px",
                              lineHeight: 1.3,
                              fontWeight: 600,
                              color: outgoing
                                ? "#075e54"
                                : "#333333",
                            }}
                          >
                            {getAuthorName(message)}
                          </MDTypography>

                          <MDTypography
                            sx={{
                              fontSize: "12px",
                              lineHeight: 1.3,
                              color: outgoing
                                ? "#367d71"
                                : "#777777",
                              textTransform: "capitalize",
                            }}
                          >
                            {getAuthorType(message)}
                          </MDTypography>
                        </MDBox>

                        {renderReply(
                          message,
                          outgoing
                        )}

                        {renderMessageContent(message)}

                        <MDBox
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                          mt={0.8}
                          ml={1}
                        >
                          <MDTypography
                            sx={{
                              fontSize: "11px",
                              color: "rgba(0, 0, 0, 0.55)",
                              lineHeight: 1,
                            }}
                          >
                            {message.msg_time || ""}
                          </MDTypography>

                          {renderStatus(message)}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  );
                })
              )}
            </MDBox>
          </Card>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsAppConversationMessagesPage;